import {
  ExDropdown, ExContainer, ButtonFlavor, ButtonType,
  ExButton, ExCombobox, ComboboxType, ComboboxSelect,
  ExPill,
  PillColor,
  ExIcon,
  IconSize,
  ExIconButton,
  IconButtonType,
  IconButtonFlavor,
  IconVariant,
} from '@boomi/exosphere';
import './FilterDropdown.scss';
import { useTranslation } from 'react-i18next';
import {
  useRef, useMemo, useCallback, useState, useEffect,
} from 'react';
import dropdownRefHandler from '@components/ToolManagement/helpers/dropdownRefHandler';

interface FilterDropdownProps {
  combobox?: {
    key: string;
    title: string;
    placeholder: string;
    options: {
      label: string;
      value: string;
      category?: string;
    }[];
  },
  filterPills: {
    label: string;
    key: string;
    options: {
      label: string;
      value: string;
    }[];
  }[];
  selectedFilters: Record<string, any>;
  isAnyFilterActive: boolean;
  setSelectedFilters: (filters: Record<string, any>) => void;
  testIdPrefix: string;
}

function FilterDropdown(props: Readonly<FilterDropdownProps>) {
  const {
    combobox, filterPills, selectedFilters, setSelectedFilters, isAnyFilterActive,
    testIdPrefix,
  } = props;
  const hasCombobox = Boolean(combobox);
  const dropdownRef = useRef<any>(null);
  const stableDropdownRef = useRef<any>(null);
  const selectedFiltersRef = useRef(selectedFilters);
  const { t } = useTranslation('common');
  const [comboboxKey, setComboboxKey] = useState(1);

  const [filters, setFilters] = useState<Record<string, any>>({});

  const selectedComboboxValues = useMemo(
    () => (hasCombobox ? filters[combobox!.key] ?? [] : []) as string[],
    [filters, hasCombobox, combobox?.key],
  );

  const closeDropdown = () => {
    const el = dropdownRef.current ?? stableDropdownRef.current;
    if (el && typeof el.closeDropdown === 'function') {
      el.closeDropdown();
    }
  };

  const handleComboboxFilterChange = useCallback((e: CustomEvent) => {
    let newToolTypes: string[];

    if (e.type === 'remove') {
      const removedLabel = e.detail?.value;

      const removedOption = combobox?.options.find(
        (option) => option.label === removedLabel,
      );

      if (removedOption) {
        newToolTypes = selectedComboboxValues.filter(
          (value) => value !== removedOption.value,
        );
      } else {
        return;
      }
    } else {
      const selectedValues = e.detail.data || e.detail.values || [];
      newToolTypes = selectedValues.map((item: any) => item.value || item);
    }
    if (combobox) {
      setFilters((prev) => ({ ...prev, [combobox.key]: newToolTypes }));
    }
  }, [selectedComboboxValues, combobox?.key, combobox?.options]);

  const handleClearFilter = useCallback((e: any) => {
    e.preventDefault();
    if (combobox) {
      setFilters((prev) => ({ ...prev, [combobox.key]: [] }));
    }
  }, [combobox]);

  const selectedEnvOptions = useMemo(
    () => (combobox?.options || []).filter((env) => selectedComboboxValues.includes(env.value)),
    [selectedComboboxValues, combobox?.options],
  );

  const handlePillClick = useCallback((key: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  const handleDropdownToggle = useCallback((isOpening: boolean) => {
    if (isOpening) {
      setFilters(selectedFiltersRef.current);
      if (hasCombobox) {
        setComboboxKey((prev) => prev + 1);
      }
    }
  }, [hasCombobox]);

  return (
    <div className="filter-dropdown-container" data-testid={`${testIdPrefix}-filter`}>
      <ExDropdown
        ref={(el) => {
          stableDropdownRef.current = el;
          dropdownRefHandler(dropdownRef, handleDropdownToggle)(el);
        }}
        icon="Filter simple"
        text=""
        flavor={ButtonFlavor.BRANDED}
        type={ButtonType.SECONDARY}
        className="filter-dropdown"
        data-testid={`${testIdPrefix}-filter-dropdown`}
      >
        <ExContainer
          className="filter-container"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="filter-header">
            <div className="filter-icon-label-container">
              <ExIcon size={IconSize.S} icon="Filter simple" variant={IconVariant.SECONDARY} />

              {t('filters')}
            </div>

            <ExIconButton
              icon="Close"
              type={IconButtonType.TERTIARY}
              flavor={IconButtonFlavor.BRANDED}
              onClick={closeDropdown}
              data-testid={`${testIdPrefix}-filter-close-button`}
            />
          </div>

          <div className="filter-body">
            {combobox && combobox.options.length > 0 && (
            <ExCombobox
              key={comboboxKey}
              placeholder={combobox.placeholder}
              data-testid={`${testIdPrefix}-filter-combobox`}
              autoComplete="off"
              menuHeight="200"
              type={ComboboxType.STANDARD}
              onChange={handleComboboxFilterChange}
              onRemove={handleComboboxFilterChange}
              select={ComboboxSelect.MULTI}
              comboboxOptions={combobox.options}
              defaultValues={selectedEnvOptions}
              showClear={selectedComboboxValues.length > 0}
              onClear={handleClearFilter}
              className="filter-combobox"
              label={combobox.title}
            />
            )}

            {filterPills.map((pill) => (
              <div key={pill.key}>
                <div className="filter-pill-label">{pill.label}</div>

                <div className="filter-pills-container">
                  {pill.options.map((option) => (
                    <ExPill
                      key={option.value}
                      color={PillColor.BLUE}
                      interactive
                      onClick={() => handlePillClick(pill.key, option.value)}
                      selected={filters[pill.key] === undefined
                        ? option.value === 'any' || option.value === 'all'
                        : filters[pill.key] === option.value}
                      data-testid={`${testIdPrefix}-filter-pill-${option.value}`}
                    >
                      {option.label}
                    </ExPill>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="filter-footer">
            <div className="filter-footer-divider" />

            <div className="filter-footer-buttons">
              <ExButton
                type={ButtonType.SECONDARY}
                flavor={ButtonFlavor.BRANDED}
                onClick={() => {
                  closeDropdown();
                  setSelectedFilters({});
                }}
                data-testid={`${testIdPrefix}-filter-clear-all-button`}
              >
                {t('clear_all')}
              </ExButton>

              <ExButton
                type={ButtonType.PRIMARY}
                flavor={ButtonFlavor.BRANDED}
                onClick={() => {
                  closeDropdown();
                  setSelectedFilters(filters);
                }}
                data-testid={`${testIdPrefix}-filter-apply-button`}
              >
                {t('apply')}
              </ExButton>
            </div>
          </div>
        </ExContainer>
      </ExDropdown>

      {isAnyFilterActive && (
        <span className="filter-indicator" />
      )}
    </div>
  );
}

export default FilterDropdown;
